import { Loader } from '@busybusy/webapp-react-ui';
import { useQueryClient } from '@tanstack/react-query';
import { Equipment } from '__generated__/graphql';
import equipment_pin from 'assets/icons/equipment_pin.svg';
import { equipmentPinDrop, equipmentPlaceholder } from 'assets/images/map-pins';
import classNames from 'classnames';
import { GoogleMap } from 'components';
import useIntegratedEquipmentEnabled from 'components/domain/equipment/IntegratedEquipmentLocationDialog/hooks/useIntegratedEquipmentEnabled';
import { useIntegratedEquipmentLastKnown } from 'components/domain/equipment/IntegratedEquipmentLocationDialog/hooks/useIntegratedEquipmentLastKnown';
import {
  convertEquipmentLocationToIntegrated,
  newestEquipmentLocation,
} from 'components/domain/equipment/IntegratedEquipmentLocationDialog/utils/utils';
import { useToastOpen } from 'contexts/ToastContext';
import gql from 'graphql-tag';
import { useApolloPaging, useOpenable, useReduxSelector } from 'hooks';
import useEquipmentDashboardNavigation from 'hooks/navigation/useEquipmentDashboardNavigation';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import 'jimp';
import { compact, first, isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigationType } from 'react-router';
import { clearManageModelMapsSelectedPin, setManageModelMapsSelectedPin } from 'store/manageModelMaps/ManageModelMaps';
import { ClassName } from 'types/ClassName';
import ICursorable from 'types/Cursorable';
import ILocation, { IClusterGroup } from 'types/Location';
import { Nullable } from 'types/util/Nullable';
import { useFeatureFlags } from 'utils/features';
import { t } from 'utils/localize';
import { isValidLocation, shiftPins } from 'utils/locationUtils';
import { getEquipmentSearch } from '../ManageEquipment';
import './ManageEquipmentMap.scss';
import ManageEquipmentMapInfoWindow, {
  MapEquipmentMapActionClasses,
} from './ManageEquipmentMapInfoWindow/ManageEquipmentMapInfoWindow';

const { Jimp } = window as Window &
  typeof globalThis & {
    Jimp: typeof import('jimp');
  };

async function getCompositeImage(url: string, pinDrop: string) {
  return new Promise<string>((res, rej) => {
    Jimp.read(url)
      .then((image) => image.resize(40, 40).circle())
      .then((image) =>
        Jimp.read(pinDrop)
          .then((pin) => {
            pin.composite(image, 4, 4).getBase64(Jimp.MIME_PNG, (err, img) => {
              if (err) {
                rej(err);
              } else {
                res(img);
              }
            });
          })
          .catch(rej)
      )
      .catch(rej);
  });
}

type ManageEquipmentMapLocation = ILocation & {
  imageUrl: Nullable<string>;
};

export interface IManageEquipmentMapProps {
  className?: ClassName;
  onDetailsClick: (equipment: Equipment) => void;
  onDirectionsClick: (equipment: Equipment) => void;
  equipmentId?: string | null;
  categoryId?: string | null;
  search?: string | null;
  updatedEquipmentId?: string;
  searchSubmitToken?: number;
}

const ManageEquipmentMap = (props: IManageEquipmentMapProps) => {
  const {
    className,
    onDetailsClick,
    onDirectionsClick,
    equipmentId,
    categoryId,
    updatedEquipmentId,
    search,
    searchSubmitToken,
  } = props;

  const loaderDetails = useOpenable();
  const toast = useToastOpen();
  const { getAll } = useApolloPaging();
  const [pins, setPins] = useState<ManageEquipmentMapLocation[]>([]);
  const mapOptions = useRef<google.maps.MapOptions>({
    center: { lat: 39, lng: -95 },
    gestureHandling: 'greedy',
    zoom: 6,
    maxZoom: 22,
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
    ],
  });
  const equipmentClusterGroup = useRef<IClusterGroup>({
    id: 'project',
    icon: equipment_pin,
  });
  const equipmentNavigation = useEquipmentDashboardNavigation();
  const navigationType = useNavigationType();
  const dispatch = useDispatch();
  const routeConsidered = useRef(false);
  const selectedLocationId = useReduxSelector((state) => state.manageModelMaps.locationId, Object.is);
  const isManagementListsSearchEnabled = useFeatureFlags('MANAGEMENT_LISTS_SEARCH');
  const integratedEquipmentEnabled = useIntegratedEquipmentEnabled();
  const fetchIntegratedLocations = useIntegratedEquipmentLastKnown();
  const queryClient = useQueryClient();
  const baseQueryKey = useReactQueryBaseKey();

  if (!routeConsidered.current && navigationType !== 'POP') {
    routeConsidered.current = true;
    dispatch(clearManageModelMapsSelectedPin());
  }

  useEffect(() => {
    getEquipment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentId, categoryId, updatedEquipmentId, search]);

  useEffect(() => {
    async function upgradeIcons() {
      if (isEmpty(pins)) {
        return;
      }

      const needsUpgrade = pins.some((pin) => {
        if (!pin.imageUrl) {
          return false;
        }
        const cached = queryClient.getQueryData<string>([...baseQueryKey, 'manage-equipment-map-image', pin.imageUrl]);
        return !cached || cached !== pin.icon;
      });

      if (!needsUpgrade) {
        return;
      }

      const upgraded = await Promise.all(
        pins.map(async (pin) => {
          const imageUrl = pin.imageUrl;
          if (!imageUrl) {
            return pin;
          }
          const icon = await queryClient.fetchQuery({
            queryKey: [...baseQueryKey, 'manage-equipment-map-image', imageUrl],
            queryFn: () => getCompositeImage(imageUrl + '?w=40&h=40', equipmentPinDrop),
            staleTime: Infinity,
          });
          if (icon === pin.icon) {
            return pin;
          }
          return { ...pin, icon };
        })
      );

      if (upgraded.some((pin, i) => pin.icon !== pins[i].icon)) {
        setPins(upgraded);
      }
    }

    upgradeIcons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pins, queryClient, baseQueryKey]);

  async function getEquipment() {
    if (isNil(selectedLocationId)) {
      loaderDetails.open();
    }

    const equipmentPromise = getAll<Equipment & ICursorable>('equipment', {
      query: MANAGE_EQUIPMENT_MAP_QUERY,
      variables: {
        first: 500,
        filter: {
          deletedOn: { isNull: true },
          model: {
            category: {
              id: categoryId ? { equal: categoryId } : undefined,
            },
          },
          id: equipmentId ? { equal: equipmentId } : undefined,
          search: isManagementListsSearchEnabled ? getEquipmentSearch(search ?? '') : undefined,
        },
      },
      fetchPolicy: 'network-only',
    });

    const placeholderIconPromise = queryClient.fetchQuery({
      queryKey: [...baseQueryKey, 'manage-equipment-map-placeholder'],
      queryFn: () => getCompositeImage(equipmentPlaceholder, equipmentPinDrop),
      staleTime: Infinity,
    });

    const [integratedLocationsData, equipment, placeholderIcon] = await Promise.all([
      fetchIntegratedLocations(integratedEquipmentEnabled),
      equipmentPromise,
      placeholderIconPromise,
    ]);

    const newPins = compact(
      equipment.map((e): ManageEquipmentMapLocation | undefined => {
        const equipmentLastLocation = !isNil(e.lastLocation)
          ? convertEquipmentLocationToIntegrated(e.lastLocation)
          : null;
        const integratedLocations = integratedLocationsData.filter((r) => r.equipmentId === e.id);
        const integratedLocation = first(integratedLocations) ?? null;
        const lastLocation = newestEquipmentLocation(equipmentLastLocation, integratedLocation);

        if (
          lastLocation?.latitude &&
          lastLocation?.longitude &&
          isValidLocation(Number(lastLocation.latitude), Number(lastLocation.longitude))
        ) {
          const cachedIcon = e.imageUrl
            ? queryClient.getQueryData<string>([...baseQueryKey, 'manage-equipment-map-image', e.imageUrl])
            : undefined;
          return {
            id: e.id,
            lat: Number(lastLocation.latitude),
            lng: Number(lastLocation.longitude),
            icon: cachedIcon ?? placeholderIcon,
            imageUrl: e.imageUrl ?? null,
            clusterGroup: equipmentClusterGroup.current,
            infoWindowBody: getEquipmentInfoWindow(e),
            actions: getEquipmentActions(e),
          };
        }
        return undefined;
      })
    );

    const shiftedPins = shiftPins(newPins);

    setPins(shiftedPins);
    if (isEmpty(shiftedPins)) {
      toast({
        label: t('No geolocation set.'),
      });
    }
    loaderDetails.close();
  }

  function getEquipmentInfoWindow(equipment: Equipment) {
    return <ManageEquipmentMapInfoWindow equipment={equipment} locationId={equipment.id} />;
  }

  const getEquipmentActions = useCallback((equipment: Equipment) => {
    return [
      {
        className: MapEquipmentMapActionClasses.EQUIPMENT_DETAILS + '-' + equipment.id,
        action: () => equipmentNavigation({ equipment_id: equipment.id }),
      },
      {
        className: MapEquipmentMapActionClasses.MANAGE_EQUIPMENT + '-' + equipment.id,
        action: () => onDetailsClick(equipment),
      },
      {
        className: MapEquipmentMapActionClasses.DIRECTIONS + '-' + equipment.id,
        action: () => onDirectionsClick(equipment),
      },
    ];
  }, []);

  function handlePinClick(location: ILocation) {
    dispatch(setManageModelMapsSelectedPin(location.id));
  }

  function handleWindowClose() {
    dispatch(clearManageModelMapsSelectedPin());
  }

  const classes = classNames('manage-equipment-map', className);

  const mapKey = useMemo(
    () =>
      pins
        .map((p) => p.id ?? '')
        .sort()
        .join('|') +
      '#' +
      (searchSubmitToken ?? 0),
    [pins, searchSubmitToken]
  );

  return (
    <>
      <GoogleMap
        key={mapKey}
        className={classes}
        mapId={'manage-equipment-map'}
        pinLocations={pins}
        options={mapOptions.current}
        selectedId={selectedLocationId}
        onPinClick={handlePinClick}
        onWindowClose={handleWindowClose}
      />
      <Loader isOpen={loaderDetails.isOpen} />
    </>
  );
};

export const MANAGE_EQUIPMENT_MAP_QUERY = gql`
  query manageEquipmentMapQuery($after: String, $filter: EquipmentFilter, $first: Int, $sort: [EquipmentSort!]) {
    equipment(after: $after, first: $first, filter: $filter, sort: $sort) {
      archivedOn
      createdOn
      cursor
      deletedOn
      equipmentGroupId
      equipmentModelId
      equipmentName
      fuelCapacity
      id
      imageId
      imageUrl
      importTtlSeconds
      importType
      lastHoursId
      lastLocationId
      lastLocation {
        id
        latitude
        longitude
      }
      model {
        category {
          deletedOn
          equipmentTypeId
          id
          imageId
          imageUrl
          review
          submittedOn
          title
          updatedOn
        }
        deletedOn
        equipmentCategoryId
        equipmentMakeId
        id
        imageId
        imageUrl
        make {
          deletedOn
          id
          imageId
          review
          submittedOn
          title
          unknown
          updatedOn
        }
        modelNumber
        submittedOn
        title
        unknown
        updatedOn
        year
      }
      organizationId
      serialNumber
      submittedOn
      trackManualHours
      updatedOn
      lastHours {
        id
        runningHours
      }
    }
  }
`;

export default ManageEquipmentMap;
